import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import {
  getStoredProgramDateTimeFormat,
  timezoneAbbreviationHelper,
} from "../../../utils/commons";
import "./dateTimePickerStyles.scss";

moment.locale("en");

const getNextDayMidnightMinDateTime = (currentDateTime) => {
  const cutoffTime = currentDateTime
    .clone()
    .hour(23)
    .minute(0)
    .second(0)
    .millisecond(0);

  return currentDateTime.isBefore(cutoffTime)
    ? currentDateTime.clone().add(1, "day").startOf("day")
    : currentDateTime.clone().add(2, "day").startOf("day");
};

const getMinimumDateTime = (minimum, timezone, currentDateTime) => {
  if (!minimum || minimum === "currentDateTime") {
    return currentDateTime;
  }

  if (minimum === "nextDayMidnight") {
    return getNextDayMidnightMinDateTime(currentDateTime);
  }

  const minDateTime = moment.tz(minimum, timezone);
  return minDateTime.isValid() ? minDateTime : currentDateTime;
};

const applyRuleValidationDateLimits = ({
  uischema,
  otherData,
  extraProps,
  timezone,
  currentDateTime,
}) => {
  const schema = uischema?.rule?.condition?.schema;
  const gtDate = otherData?.[schema?.gt];
  const ltDate = otherData?.[schema?.lt];

  if (schema?.gt && gtDate && !Number.isNaN(new Date(gtDate))) {
    const gtDateObj = new Date(new Date(gtDate).getTime() + 60000);
    const gtMoment = moment.tz(gtDateObj, timezone);
    extraProps.minDateTime = currentDateTime.isAfter(gtMoment)
      ? currentDateTime
      : gtMoment;
  }

  if (schema?.lt && ltDate && !isNaN(new Date(ltDate))) {
    extraProps.maxDateTime = moment.tz(new Date(ltDate), timezone);
  }
};

const addExtraProps = ({ uischema, otherData, extraProps, timezone }) => {
  const currentDateTime = moment.tz(new Date(), timezone);

  if (uischema?.minimum) {
    extraProps.minDateTime = getMinimumDateTime(
      uischema.minimum,
      timezone,
      currentDateTime
    );
  }

  if (uischema?.maximum) {
    const maxDateTime = moment.tz(uischema.maximum, timezone);
    if (maxDateTime.isValid()) {
      extraProps.maxDateTime = maxDateTime;
    }
  }

  if (uischema?.rule?.effect === "VALIDATE") {
    applyRuleValidationDateLimits({
      uischema,
      otherData,
      extraProps,
      timezone,
      currentDateTime,
    });
  }
};

/*
  @label: label for dateTimePicker
  @dateTime: current Date Time Value that is set.
  @setDateTime: function to set the new Date Time Value. Will always return UTC format.
  @minimum: minimum date time value that can be selected.
  @maximum: maximum date time value that can be selected.
*/

export default function DateTimePickerWithTimeZone({
  label,
  dateTime,
  setDateTime,
  timezone,
  format = "utc_string",
  disabled,
  uischema,
  className,
  otherData,
  slotProps,
  errors,
  showValidation,
}) {
  /* convert the selected date time into UTC/UNIX format  */
  const handleAccept = (date) => {
    if (date) {
      const formatDate =
        format === "utc_string" ? date.toISOString() : date.unix() * 1000;
      setDateTime(formatDate);
    } else {
      setDateTime(null);
    }
  };

  const extraProps = {
    minDateTime: undefined,
    maxDateTime: undefined,
  };

  addExtraProps({ uischema, otherData, extraProps, disabled, timezone });

  return (
    <div
      data-testid="date-time-picker-container"
      className={`display-flex my-1 flex-direction-column gap-2 ${className || ""}`}
    >
      <label
        style={{ fontSize: "12px", color: "#3A3A3A" }}
        htmlFor={`datePicker-${label}`}
      >
        {label} {timezone ? <>({timezoneAbbreviationHelper(timezone)})</> : ""}
      </label>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        dateLibInstance={moment}
        adapterLocale="en"
      >
        <DateTimePicker
          timezone={timezone}
          className="customDatePickerStyles"
          value={dateTime ? moment.tz(dateTime, timezone) : null}
          onAccept={handleAccept}
          timeSteps={{ minutes: 1 }}
          disabled={disabled}
          {...extraProps}
          slotProps={slotProps}
          closeOnSelect={false}
          format={getStoredProgramDateTimeFormat()}
          desktopModeMediaQuery="@media (min-height: 640px)"
        />
      </LocalizationProvider>
      {showValidation && errors && (
        <div style={{ fontSize: "12px", color: "#d32f2f", marginTop: "4px" }}>
          {errors}
        </div>
      )}
    </div>
  );
}

DateTimePickerWithTimeZone.propTypes = {
  label: PropTypes.string,
  dateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setDateTime: PropTypes.func.isRequired,
  timezone: PropTypes.string,
  format: PropTypes.oneOf(["utc_string", "unix"]),
  disabled: PropTypes.bool,
  uischema: PropTypes.shape({
    minimum: PropTypes.string,
    maximum: PropTypes.string,
    rule: PropTypes.shape({
      effect: PropTypes.string,
      condition: PropTypes.shape({
        schema: PropTypes.shape({
          gt: PropTypes.string,
          lt: PropTypes.string,
        }),
      }),
    }),
  }),
  className: PropTypes.string,
  otherData: PropTypes.object,
  slotProps: PropTypes.object,
};
